import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { Users, Plus, Edit2, Trash2, Mail, Shield, ShieldOff, Percent, FileText, FileCheck, ShoppingCart, Eye, EyeOff, KeyRound, Loader2, Clock, UserPlus, Crown, AlertTriangle, RefreshCw, Info, Phone, MapPin, Building, Calculator } from 'lucide-react';
import { BroadcastSender } from '../components/BroadcastSender';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// RUOLI DISPONIBILI - Super Admin ESCLUSO (ruolo unico e protetto)
const ROLES = [
  { value: 'admin', label: 'Amministratore', color: 'bg-red-100 text-red-700' },
  { value: 'top_agent', label: 'Top Agent', color: 'bg-amber-100 text-amber-700' },
  { value: 'agente', label: 'Agente', color: 'bg-blue-100 text-blue-700' }
  // SURGELATO - Fase 1: Ruolo Cliente nascosto dalla creazione utenti
  // { value: 'cliente', label: 'Cliente', color: 'bg-slate-100 text-slate-700' }
];

// Helper: ottiene i ruoli disponibili in base a chi sta modificando
const getAvailableRoles = (currentUser) => {
  // Super Admin: può assegnare admin, top_agent, agente (ma NON super_admin)
  if (currentUser?.is_super_admin) {
    return ROLES;
  }
  
  // Top Agent o Admin di reparto: possono solo assegnare admin o agente (NO top_agent)
  // La nomina a Top Agent avviene SOLO dalla pagina Reparti
  return ROLES.filter(r => r.value !== 'top_agent');
};

// Helper: verifica se l'utente corrente può modificare il target user
const canEditUser = (currentUser, targetUser) => {
  // Il Super Admin può modificare chiunque
  if (currentUser?.is_super_admin) return true;
  
  // GERARCHIA: SUPER_ADMIN > TOP_AGENT > ADMIN > AGENTE
  const currentRole = currentUser?.role;
  const targetRole = targetUser?.role;
  const currentReparto = currentUser?.reparto_id;
  const targetReparto = targetUser?.reparto_id;
  
  // Un utente NON può modificare il Super Admin
  if (targetUser?.is_super_admin) return false;
  
  // Un utente NON può modificare un Top Agent (solo Super Admin può)
  if (targetRole === 'top_agent') return false;
  
  // ADMIN: Solo Top Agent (dello stesso reparto) o Super Admin può modificare
  if (targetRole === 'admin') {
    // Un Admin può modificare solo se stesso
    if (currentRole === 'admin') {
      return targetUser?.id === currentUser?.id;
    }
    // Un Agente non può modificare Admin
    if (currentRole === 'agente') return false;
    // Top Agent può modificare Admin solo del suo reparto
    if (currentRole === 'top_agent') {
      return currentReparto === targetReparto;
    }
  }
  
  // REPARTO: Non si può modificare utenti di altri reparti
  if (currentReparto !== targetReparto && currentReparto !== 'reparto-0-sede-centrale') {
    return false;
  }
  
  // Tutti gli altri casi: può modificare (agenti, etc.)
  return true;
};

// Helper: verifica se l'utente corrente può eliminare il target user
const canDeleteUser = (currentUser, targetUser) => {
  // Non puoi eliminare te stesso
  if (targetUser?.id === currentUser?.id) return false;
  
  // Il Super Admin non può essere eliminato
  if (targetUser?.is_super_admin) return false;
  
  // Il Super Admin può eliminare chiunque (tranne se stesso)
  if (currentUser?.is_super_admin) return true;
  
  const currentRole = currentUser?.role;
  const targetRole = targetUser?.role;
  const currentReparto = currentUser?.reparto_id;
  const targetReparto = targetUser?.reparto_id;
  
  // Un utente NON può eliminare un Top Agent (solo Super Admin)
  if (targetRole === 'top_agent') return false;
  
  // ADMIN: Solo Top Agent (dello stesso reparto) può eliminare
  if (targetRole === 'admin') {
    if (currentRole === 'top_agent' && currentReparto === targetReparto) {
      return true;
    }
    return false;
  }
  
  // REPARTO: Non si può eliminare utenti di altri reparti
  if (currentReparto !== targetReparto && currentReparto !== 'reparto-0-sede-centrale') {
    return false;
  }
  
  // Agenti possono essere eliminati da Admin e Top Agent del reparto
  return ['admin', 'top_agent'].includes(currentRole);
};

const UsersPage = () => {
  const { getAuthHeader, user: currentUser, impersonatedUser, getImpersonateParams } = useAuth();
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({});  // Statistiche produttività agenti
  const [userActivity, setUserActivity] = useState({});  // KPI: Dettagli e Calcoli (30g)
  const [recentAuthorized, setRecentAuthorized] = useState([]);  // Widget ultimi autorizzati
  const [reparti, setReparti] = useState([]);  // Lista reparti per mapping ID -> Nome
  const [searchQuery, setSearchQuery] = useState('');  // Filtro ricerca testuale
  const [sortBy, setSortBy] = useState('created_at');  // Ordinamento: created_at (default), name, authorized_at
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);  // Toggle visibilità password
  const [isSaving, setIsSaving] = useState(false);  // Protezione double-click
  const [actionLoading, setActionLoading] = useState(null);  // Per azioni singole (delete, toggle)
  const [availableCapogruppo, setAvailableCapogruppo] = useState([]);  // Lista capogruppo disponibili
  const [cascadeDialog, setCascadeDialog] = useState({ open: false, userId: null, newValue: null });  // Dialog conferma cascade
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'agente', // SURGELATO - Fase 1: Default cambiato da 'cliente' a 'agente'
    commission_rate: 6,
    competenza_provvigioni: 30,
    capogruppo_id: '',
    // Campi anagrafici
    telefono: '',
    partita_iva: '',
    codice_fiscale: '',
    indirizzo: '',
    citta: '',
    provincia: '',
    reparto_id: 'reparto-0-sede-centrale'  // Default: Sede Centrale
  });

  // Costruisce i parametri per le API includendo view_as se in impersonificazione
  const getApiParams = useCallback(() => {
    const params = getImpersonateParams();
    return params.view_as ? `?view_as=${params.view_as}` : '';
  }, [getImpersonateParams]);

  const fetchUsers = useCallback(async () => {
    try {
      const queryParams = getApiParams();
      const [usersRes, statsRes, recentRes, repartiRes, activityRes] = await Promise.all([
        axios.get(`${API}/users${queryParams}`, { headers: getAuthHeader() }),
        axios.get(`${API}/users/stats${queryParams}`, { headers: getAuthHeader() }),
        axios.get(`${API}/users/recent-authorized?limit=10`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/reparti`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/kpi/users-activity${queryParams}`, { headers: getAuthHeader() }).catch(() => ({ data: {} }))
      ]);
      // ========================================
      // SURGELATO - Fase 1: Filtro solo Admin, Top Agent e Agenti
      // I clienti rimangono nel database ma non vengono mostrati
      // Per riattivare: rimuovere il filtro sotto
      // ========================================
      const filteredUsers = usersRes.data.filter(u => 
        u.role === 'admin' || u.role === 'agente' || u.role === 'top_agent' || u.role === 'super_admin'
      );
      setUsers(filteredUsers);
      setUserStats(statsRes.data || {});
      setRecentAuthorized(recentRes.data || []);
      setReparti(repartiRes.data || []);
      setUserActivity(activityRes.data || {});
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, getApiParams]);

  // Ricarica quando cambia l'impersonificazione
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, impersonatedUser]);

  // Helper per ottenere nome reparto da ID
  const getRepartoName = (repartoId) => {
    if (!repartoId) return 'Non assegnato';
    if (repartoId === 'reparto-0-sede-centrale') return 'Sede Centrale';
    const reparto = reparti.find(r => r.id === repartoId);
    return reparto ? reparto.nome : 'Non assegnato';
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'agente', // SURGELATO - Fase 1: Default cambiato da 'cliente' a 'agente'
      commission_rate: 6,
      competenza_provvigioni: 30,
      capogruppo_id: '',
      telefono: '',
      partita_iva: '',
      codice_fiscale: '',
      indirizzo: '',
      citta: '',
      provincia: '',
      reparto_id: 'reparto-0-sede-centrale'
    });
    setEditingUser(null);
    setAvailableCapogruppo([]);
  };

  const handleOpenDialog = async (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        password: '',  // Campo vuoto per modifica - non pre-compilare
        role: user.role,
        commission_rate: user.commission_rate,
        competenza_provvigioni: user.competenza_provvigioni || 30,
        capogruppo_id: user.capogruppo_id || '',
        telefono: user.telefono || '',
        partita_iva: user.partita_iva || '',
        codice_fiscale: user.codice_fiscale || '',
        indirizzo: user.indirizzo || '',
        citta: user.citta || '',
        provincia: user.provincia || '',
        reparto_id: user.reparto_id || 'reparto-0-sede-centrale'
      });
      
      // Carica capogruppo disponibili se è un agente
      if (user.role === 'agente') {
        try {
          const res = await axios.get(`${API}/agents/available-capogruppo`, {
            params: { agent_id: user.id },
            headers: getAuthHeader()
          });
          setAvailableCapogruppo(res.data || []);
        } catch (err) {
          console.error('Error loading capogruppo:', err);
          setAvailableCapogruppo([]);
        }
      }
    } else {
      resetForm();
    }
    setShowPassword(false);  // Reset visibilità password
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setShowPassword(false);  // Reset visibilità password
    resetForm();
  };

  // Funzione per caricare capogruppo filtrati per provvigione
  const loadCapogruppoByProvvigioni = async (provvigioni) => {
    if (!editingUser || editingUser.role !== 'agente') return;
    
    try {
      const res = await axios.get(`${API}/agents/available-capogruppo`, {
        params: { 
          agent_id: editingUser.id,
          provvigioni: provvigioni
        },
        headers: getAuthHeader()
      });
      setAvailableCapogruppo(res.data || []);
    } catch (err) {
      console.error('Error loading capogruppo:', err);
      setAvailableCapogruppo([]);
    }
  };

  // Gestione cambio provvigioni con verifica cascade
  const handleProvvigioniChange = async (value) => {
    const newValue = value[0];
    
    // Se è un capogruppo con subordinati, chiedi conferma per cascade
    if (editingUser && editingUser.subordinati_count > 0 && newValue !== formData.competenza_provvigioni) {
      setCascadeDialog({
        open: true,
        userId: editingUser.id,
        newValue: newValue
      });
      return;
    }
    
    // Se ha un capogruppo, blocca la modifica
    if (editingUser && editingUser.capogruppo_id && formData.capogruppo_id) {
      toast.error('Impossibile modificare la percentuale di un agente legato a un gruppo. Rimuovi prima il Capogruppo.');
      return;
    }
    
    setFormData(prev => ({ ...prev, competenza_provvigioni: newValue }));
    
    // Ricarica capogruppo compatibili
    if (editingUser?.role === 'agente') {
      await loadCapogruppoByProvvigioni(newValue);
    }
  };

  // Conferma cascade update provvigioni
  const handleConfirmCascade = async () => {
    try {
      await axios.post(
        `${API}/users/${cascadeDialog.userId}/cascade-provvigioni?new_provvigioni=${cascadeDialog.newValue}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Percentuale aggiornata per il gruppo`);
      setFormData(prev => ({ ...prev, competenza_provvigioni: cascadeDialog.newValue }));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setCascadeDialog({ open: false, userId: null, newValue: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Protezione double-click
    if (isSaving) {
      console.warn('Salvataggio già in corso, richiesta ignorata');
      return;
    }
    
    setIsSaving(true);
    
    try {
      if (editingUser) {
        // Prepara i dati per l'update - include password solo se compilata
        const updatePayload = {
          name: formData.name,
          role: formData.role,
          commission_rate: formData.commission_rate,
          competenza_provvigioni: formData.competenza_provvigioni,
          reparto_id: formData.reparto_id
        };
        
        // Aggiungi capogruppo_id se è un agente
        if (editingUser.role === 'agente') {
          // Invia stringa vuota se "Nessun capogruppo": il backend la interpreta come
          // richiesta esplicita di rimozione (esegue $unset). Inviando null Pydantic
          // marcava il campo come "non modificato" e la rimozione veniva ignorata.
          updatePayload.capogruppo_id = formData.capogruppo_id || '';
        }
        
        // Aggiungi password solo se l'admin l'ha inserita
        if (formData.password && formData.password.trim() !== '') {
          updatePayload.password = formData.password;
        }
        
        // Campi anagrafici (Super Admin può modificare tutto)
        if (currentUser?.is_super_admin) {
          if (formData.email && formData.email !== editingUser.email) {
            updatePayload.email = formData.email;
          }
          updatePayload.telefono = formData.telefono || null;
          updatePayload.partita_iva = formData.partita_iva || null;
          updatePayload.codice_fiscale = formData.codice_fiscale || null;
          updatePayload.indirizzo = formData.indirizzo || null;
          updatePayload.citta = formData.citta || null;
          updatePayload.provincia = formData.provincia || null;
        }
        
        await axios.put(`${API}/users/${editingUser.id}`, updatePayload, { headers: getAuthHeader() });
        toast.success(formData.password ? 'Utente e password aggiornati' : 'Utente aggiornato');
      } else {
        await axios.post(`${API}/users`, formData, { headers: getAuthHeader() });
        toast.success('Utente creato');
      }
      
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Errore nel salvataggio';
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    // Protezione double-click
    if (actionLoading === userId) return;
    
    setActionLoading(userId);
    
    try {
      await axios.put(`${API}/users/${userId}`, {
        is_active: !currentStatus
      }, { headers: getAuthHeader() });
      
      toast.success(currentStatus ? 'Utente disattivato' : 'Utente attivato');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) return;
    
    // Protezione double-click
    if (actionLoading === userId) return;
    
    setActionLoading(userId);
    
    try {
      await axios.delete(`${API}/users/${userId}`, { headers: getAuthHeader() });
      toast.success('Utente eliminato');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    } finally {
      setActionLoading(null);
    }
  };

  // Sblocca quota offerte per un agente
  const handleResetQuota = async (userId, userName) => {
    if (!window.confirm(`Sbloccare le quote per ${userName}? Il contatore verrà azzerato e il limite aumentato di 20.`)) return;
    
    setActionLoading(userId);
    try {
      const res = await axios.post(`${API}/admin/reset-quota/${userId}`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message || 'Quota sbloccata');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nello sblocco quota');
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleBadge = (role) => {
    const roleInfo = ROLES.find(r => r.value === role) || ROLES[2];
    return <Badge className={roleInfo.color}>{roleInfo.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Broadcast Sender - Solo Super Admin */}
      {currentUser?.is_super_admin && <BroadcastSender />}
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Gestione Utenti
          </h1>
          <p className="text-slate-500 mt-1">Gestisci gli utenti e i loro permessi</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Utente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
                {editingUser ? 'Modifica Utente' : 'Nuovo Utente'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nome completo</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="user-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={editingUser}
                  data-testid="user-email-input"
                />
              </div>
              
              {!editingUser && (
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                      className="pr-10"
                      data-testid="user-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Campo password per MODIFICA utente - opzionale */}
              {editingUser && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-amber-500" />
                    Nuova Password
                    <span className="text-xs text-slate-400 font-normal">(lascia vuoto per non modificare)</span>
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      minLength={6}
                      className="pr-10"
                      data-testid="user-new-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-amber-600">
                    ⚠️ La nuova password sovrascriverà quella attuale senza conferme
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(v) => setFormData({ ...formData, role: v })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableRoles(currentUser).map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Competenza Provvigioni - solo per agenti */}
              {formData.role === 'agente' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Competenza Provvigioni</Label>
                    <span className="text-lg font-bold text-blue-600">{formData.competenza_provvigioni}%</span>
                  </div>
                  <Slider
                    value={[formData.competenza_provvigioni]}
                    onValueChange={handleProvvigioniChange}
                    min={30}
                    max={100}
                    step={5}
                    className="py-2"
                    data-testid="user-competenza-slider"
                    disabled={editingUser?.capogruppo_id && formData.capogruppo_id}
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>30%</span>
                    <span>100%</span>
                  </div>
                  {editingUser?.capogruppo_id && formData.capogruppo_id && (
                    <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                      ⚠️ Per modificare la percentuale, rimuovi prima il Capogruppo
                    </p>
                  )}
                  {editingUser?.subordinati_count > 0 && (
                    <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      Capogruppo di {editingUser.subordinati_count} collaboratori - le modifiche si applicheranno a tutto il gruppo
                    </p>
                  )}
                </div>
              )}

              {/* Capogruppo - solo per agenti in modifica */}
              {formData.role === 'agente' && editingUser && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-amber-500" />
                    Capogruppo
                  </Label>
                  <Select
                    value={formData.capogruppo_id || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, capogruppo_id: value === 'none' ? '' : value })}
                    disabled={editingUser?.subordinati_count > 0}
                  >
                    <SelectTrigger data-testid="capogruppo-select">
                      <SelectValue placeholder="Nessun Capogruppo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun Capogruppo</SelectItem>
                      {availableCapogruppo
                        .filter(cg => cg.competenza_provvigioni === formData.competenza_provvigioni)
                        .map(cg => (
                          <SelectItem key={cg.id} value={cg.id}>
                            {cg.name} ({cg.competenza_provvigioni}%)
                            {cg.subordinati_count > 0 && ` - ${cg.subordinati_count} membri`}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {editingUser?.subordinati_count > 0 && (
                    <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                      ⚠️ Questo agente è Capogruppo di {editingUser.subordinati_count} collaboratori. Non può essere assegnato a un altro Capogruppo.
                    </p>
                  )}
                  {availableCapogruppo.filter(cg => cg.competenza_provvigioni === formData.competenza_provvigioni).length === 0 && 
                   !editingUser?.subordinati_count && (
                    <p className="text-xs text-slate-500">
                      Nessun agente disponibile con la stessa percentuale ({formData.competenza_provvigioni}%)
                    </p>
                  )}
                </div>
              )}
              
              {/* ============== SEZIONE DATI ANAGRAFICI (Solo Super Admin in modifica) ============== */}
              {editingUser && currentUser?.is_super_admin && (
                <div className="space-y-4 border-t pt-4 mt-4">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Building className="w-4 h-4" />
                    <span className="font-medium text-sm">Dati Anagrafici / Fiscali</span>
                  </div>
                  
                  {/* REPARTO DI APPARTENENZA */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-purple-500" />
                      Reparto di Appartenenza
                    </Label>
                    <Select 
                      value={formData.reparto_id} 
                      onValueChange={(value) => setFormData({ ...formData, reparto_id: value })}
                    >
                      <SelectTrigger data-testid="user-reparto-select">
                        <SelectValue placeholder="Seleziona reparto" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reparto-0-sede-centrale">
                          🏢 Sede Centrale (MAcheNoleggio)
                        </SelectItem>
                        {reparti.filter(r => r.id !== 'reparto-0-sede-centrale').map(r => (
                          <SelectItem key={r.id} value={r.id}>
                            🏪 {r.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Sposta l'utente in un altro reparto per gestire la visibilità e le provvigioni
                    </p>
                  </div>
                  
                  {/* Email (modificabile solo da Super Admin) */}
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="user-email-edit"
                    />
                  </div>
                  
                  {/* Telefono */}
                  <div className="space-y-2">
                    <Label>Telefono</Label>
                    <Input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      placeholder="es. 348 1234567"
                      data-testid="user-telefono-input"
                    />
                  </div>
                  
                  {/* Partita IVA / Codice Fiscale */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Partita IVA</Label>
                      <Input
                        value={formData.partita_iva}
                        onChange={(e) => setFormData({ ...formData, partita_iva: e.target.value })}
                        placeholder="12345678901"
                        data-testid="user-piva-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Codice Fiscale</Label>
                      <Input
                        value={formData.codice_fiscale}
                        onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value.toUpperCase() })}
                        placeholder="RSSMRA80A01H501U"
                        data-testid="user-cf-input"
                      />
                    </div>
                  </div>
                  
                  {/* Indirizzo */}
                  <div className="space-y-2">
                    <Label>Indirizzo</Label>
                    <Input
                      value={formData.indirizzo}
                      onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                      placeholder="Via Roma, 1"
                      data-testid="user-indirizzo-input"
                    />
                  </div>
                  
                  {/* Città / Provincia */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Città</Label>
                      <Input
                        value={formData.citta}
                        onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                        placeholder="Roma"
                        data-testid="user-citta-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Provincia</Label>
                      <Input
                        value={formData.provincia}
                        onChange={(e) => setFormData({ ...formData, provincia: e.target.value.toUpperCase() })}
                        placeholder="RM"
                        maxLength={2}
                        data-testid="user-provincia-input"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={handleCloseDialog} disabled={isSaving}>
                  Annulla
                </Button>
                <Button type="submit" className="flex-1" data-testid="save-user-btn" disabled={isSaving}>
                  {isSaving ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />Salvataggio...</>
                  ) : (
                    editingUser ? 'Salva modifiche' : 'Crea utente'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Conferma Cascade Update Provvigioni */}
        <Dialog open={cascadeDialog.open} onOpenChange={(open) => !open && setCascadeDialog({ open: false, userId: null, newValue: null })}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <Crown className="w-5 h-5" />
                Modifica Gruppo
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <p className="text-slate-600">
                Questa modifica verrà applicata a <strong>tutti i collaboratori del gruppo</strong>.
              </p>
              <p className="text-slate-600">
                La nuova percentuale sarà: <strong className="text-blue-600">{cascadeDialog.newValue}%</strong>
              </p>
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setCascadeDialog({ open: false, userId: null, newValue: null })}
                >
                  Annulla
                </Button>
                <Button 
                  className="flex-1 bg-amber-500 hover:bg-amber-600"
                  onClick={handleConfirmCascade}
                >
                  Conferma Modifica
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Widget: Ultimi Autorizzati */}
      {recentAuthorized.length > 0 && (
        <Card className="mb-6 border-green-200 bg-green-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-green-700 text-base">
              <Clock className="w-4 h-4" />
              Ultimi 10 Utenti Autorizzati
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {recentAuthorized.map((u, idx) => (
                <Badge 
                  key={u.id} 
                  variant="outline" 
                  className={`cursor-pointer hover:bg-green-100 transition-colors ${idx === 0 ? 'bg-green-100 border-green-400' : ''}`}
                  onClick={() => {
                    const userInList = users.find(usr => usr.id === u.id);
                    if (userInList) handleOpenDialog(userInList);
                  }}
                >
                  <span className="font-medium">{u.name || u.email}</span>
                  {u.authorized_at && (
                    <span className="ml-1 text-xs text-slate-500">
                      ({new Date(u.authorized_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})
                    </span>
                  )}
                  {!u.authorized_at && u.created_at && (
                    <span className="ml-1 text-xs text-slate-500">
                      ({new Date(u.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })})
                    </span>
                  )}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtri e Ordinamento */}
      <Card className="mb-4">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Ricerca */}
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="🔍 Cerca per nome, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
            
            {/* Ordinamento */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600 whitespace-nowrap">Ordina per:</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">📅 Data Iscrizione (recenti)</SelectItem>
                  <SelectItem value="authorized_at">✅ Data Autorizzazione</SelectItem>
                  <SelectItem value="name">🔤 Nome (A-Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filtro rapido alfabetico */}
            <div className="flex gap-1 flex-wrap">
              {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'L', 'M', 'N', 'O', 'P', 'R', 'S', 'T', 'V', 'Z'].map(letter => (
                <Button
                  key={letter}
                  variant={searchQuery.toUpperCase() === letter ? 'default' : 'outline'}
                  size="sm"
                  className="w-8 h-8 p-0 text-xs"
                  onClick={() => setSearchQuery(searchQuery.toUpperCase() === letter ? '' : letter)}
                >
                  {letter}
                </Button>
              ))}
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-red-500"
                  onClick={() => setSearchQuery('')}
                >
                  ✕ Reset
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Elenco Utenti
            <Badge variant="secondary" className="ml-2 text-sm font-normal">
              {users.length} credenziali
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Utente</TableHead>
                    <TableHead>Ruolo</TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Clock className="w-4 h-4" />
                        Iscrizione
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <UserPlus className="w-4 h-4" />
                        Autorizzato
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-1">
                        <Building className="w-4 h-4" />
                        Reparto
                      </div>
                    </TableHead>
                    <TableHead>Competenza Provv.</TableHead>
                    <TableHead>Capogruppo</TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <FileText className="w-4 h-4" />
                        Offerte
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <FileCheck className="w-4 h-4" />
                        Contratti
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <ShoppingCart className="w-4 h-4" />
                        Ordini
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex flex-col items-center justify-center gap-0.5">
                        <Eye className="w-4 h-4 text-blue-500" />
                        <span className="text-[10px] text-muted-foreground">Dettagli</span>
                        <span className="text-[9px] text-slate-400">(30g)</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex flex-col items-center justify-center gap-0.5">
                        <Calculator className="w-4 h-4 text-green-500" />
                        <span className="text-[10px] text-muted-foreground">Calcoli</span>
                        <span className="text-[9px] text-slate-400">(30g)</span>
                      </div>
                    </TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users
                    // Filtro per ricerca
                    .filter(user => {
                      if (!searchQuery) return true;
                      const query = searchQuery.toLowerCase();
                      // Se è una singola lettera, filtra per iniziale del nome
                      if (query.length === 1) {
                        return user.name?.toLowerCase().startsWith(query);
                      }
                      // Altrimenti cerca in nome ed email
                      return (
                        user.name?.toLowerCase().includes(query) ||
                        user.email?.toLowerCase().includes(query)
                      );
                    })
                    // Ordinamento
                    .sort((a, b) => {
                      if (sortBy === 'name') {
                        return (a.name || '').localeCompare(b.name || '');
                      }
                      if (sortBy === 'authorized_at') {
                        const dateA = a.authorized_at || a.created_at || '';
                        const dateB = b.authorized_at || b.created_at || '';
                        return dateB.localeCompare(dateA); // Più recenti prima
                      }
                      // Default: created_at (più recenti prima)
                      const dateA = a.created_at || '';
                      const dateB = b.created_at || '';
                      return dateB.localeCompare(dateA);
                    })
                    .map(user => {
                    const stats = userStats[user.id] || { total_offers: 0, contracts_signed: 0, orders_completed: 0 };
                    return (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`} className={user.status === 'pending' ? 'bg-amber-50/50' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {/* Avatar con icona distintiva per pending */}
                          <div className={`relative w-10 h-10 rounded-full flex items-center justify-center ${
                            user.status === 'pending' ? 'bg-amber-100' : 'bg-primary/10'
                          }`}>
                            <span className={`text-sm font-semibold ${
                              user.status === 'pending' ? 'text-amber-700' : 'text-primary'
                            }`}>
                              {user.name?.charAt(0).toUpperCase()}
                            </span>
                            {/* Icona scudetto barrato per pending */}
                            {user.status === 'pending' && (
                              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center border-2 border-white">
                                <ShieldOff className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-800">{user.name}</p>
                              {/* Badge "In Attesa" per utenti pending */}
                              {user.status === 'pending' && (
                                <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-xs flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  In Attesa
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {user.status === 'pending' ? (
                            <ShieldOff className="w-4 h-4 text-red-400" />
                          ) : (
                            <Shield className="w-4 h-4 text-slate-400" />
                          )}
                          {getRoleBadge(user.role)}
                        </div>
                      </TableCell>
                      {/* Data Iscrizione */}
                      <TableCell className="text-center">
                        {user.created_at ? (
                          <div className="text-xs text-slate-600">
                            <div>{new Date(user.created_at).toLocaleDateString('it-IT')}</div>
                            <div className="text-slate-400">{new Date(user.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      {/* Data Autorizzazione */}
                      <TableCell className="text-center">
                        {user.authorized_at ? (
                          <div className="text-xs text-green-600">
                            <div>{new Date(user.authorized_at).toLocaleDateString('it-IT')}</div>
                            <div className="text-green-500">{new Date(user.authorized_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        ) : user.is_active ? (
                          <span className="text-xs text-slate-400">(pre-tracking)</span>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">Da autorizzare</Badge>
                        )}
                      </TableCell>
                      {/* Reparto */}
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            !user.reparto_id || user.reparto_id === 'reparto-0-sede-centrale' 
                              ? 'border-blue-300 text-blue-600 bg-blue-50' 
                              : 'border-purple-300 text-purple-600 bg-purple-50'
                          }`}
                        >
                          {getRepartoName(user.reparto_id)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.role === 'agente' ? (
                          <div className="flex items-center gap-1">
                            <Percent className="w-4 h-4 text-blue-500" />
                            <span className="font-medium text-blue-600">{user.competenza_provvigioni || 30}%</span>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      {/* Capogruppo */}
                      <TableCell>
                        {user.role === 'agente' ? (
                          user.subordinati_count > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-1 w-fit">
                              <Crown className="w-3 h-3" />
                              Capogruppo ({user.subordinati_count})
                            </Badge>
                          ) : user.capogruppo_name ? (
                            <span className="text-sm text-slate-600 flex items-center gap-1">
                              <UserPlus className="w-3 h-3 text-slate-400" />
                              {user.capogruppo_name}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      {/* Statistiche Produttività */}
                      <TableCell className="text-center">
                        {user.role === 'agente' ? (
                          <div className="flex flex-col items-center gap-1">
                            <Badge variant="outline" className="bg-slate-50">
                              {stats.total_offers}
                            </Badge>
                            {/* Indicatore quota */}
                            {user.quotazioni_count >= (user.quota_limit || 10) ? (
                              <span className="text-xs text-orange-600 flex items-center gap-1" title="Quota raggiunta - clicca Sblocca">
                                <AlertTriangle className="w-3 h-3" />
                                {user.quotazioni_count}/{user.quota_limit || 10}
                              </span>
                            ) : user.quotazioni_count > 0 ? (
                              <span className="text-xs text-slate-400">
                                {user.quotazioni_count}/{user.quota_limit || 10}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {user.role === 'agente' ? (
                          <Badge variant="outline" className={stats.contracts_signed > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50"}>
                            {stats.contracts_signed}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {user.role === 'agente' ? (
                          <Badge variant="outline" className={stats.orders_completed > 0 ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50"}>
                            {stats.orders_completed}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      {/* KPI: Visualizzazioni Dettagli (30g) */}
                      <TableCell className="text-center">
                        {['agente', 'top_agent'].includes(user.role) ? (
                          <Badge 
                            variant="outline" 
                            className={
                              (userActivity[user.id]?.view_details_count || 0) > 10 
                                ? "bg-blue-50 text-blue-700 border-blue-200" 
                                : (userActivity[user.id]?.view_details_count || 0) > 0 
                                  ? "bg-slate-50" 
                                  : "bg-red-50 text-red-400 border-red-200"
                            }
                          >
                            {userActivity[user.id]?.view_details_count || 0}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      {/* KPI: Calcoli Canone (30g) */}
                      <TableCell className="text-center">
                        {['agente', 'top_agent'].includes(user.role) ? (
                          <Badge 
                            variant="outline" 
                            className={
                              (userActivity[user.id]?.calculate_quote_count || 0) > 5 
                                ? "bg-green-50 text-green-700 border-green-200" 
                                : (userActivity[user.id]?.calculate_quote_count || 0) > 0 
                                  ? "bg-slate-50" 
                                  : "bg-red-50 text-red-400 border-red-200"
                            }
                          >
                            {userActivity[user.id]?.calculate_quote_count || 0}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Super Admin: mostra badge invece dello switch */}
                        {user.is_super_admin ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300">
                            🛡️ Super Admin
                          </Badge>
                        ) : !canEditUser(currentUser, user) ? (
                          <Badge className="bg-slate-100 text-slate-600 border border-slate-300">
                            🔒 Protetto
                          </Badge>
                        ) : (
                          <Switch
                            checked={user.is_active}
                            onCheckedChange={() => handleToggleActive(user.id, user.is_active)}
                            disabled={user.id === currentUser?.id}
                            data-testid={`toggle-active-${user.id}`}
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Pulsante Info Anagrafica (Popover) */}
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                title="Dettagli Anagrafici"
                                data-testid={`info-user-${user.id}`}
                              >
                                <Info className="w-4 h-4" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80" align="end">
                              <div className="space-y-3">
                                <div className="flex items-center gap-2 border-b pb-2">
                                  <Building className="w-4 h-4 text-slate-500" />
                                  <span className="font-semibold text-sm">Dati Anagrafici</span>
                                </div>
                                
                                {/* Telefono */}
                                <div className="flex items-center gap-2 text-sm">
                                  <Phone className="w-4 h-4 text-slate-400" />
                                  <span className="text-slate-600">Telefono:</span>
                                  <span className="font-medium">{user.telefono || '—'}</span>
                                </div>
                                
                                {/* P.IVA / CF */}
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div>
                                    <span className="text-slate-500 text-xs">P.IVA</span>
                                    <p className="font-medium">{user.partita_iva || user.piva || '—'}</p>
                                  </div>
                                  <div>
                                    <span className="text-slate-500 text-xs">Cod. Fiscale</span>
                                    <p className="font-medium text-xs">{user.codice_fiscale || '—'}</p>
                                  </div>
                                </div>
                                
                                {/* Indirizzo */}
                                <div className="text-sm">
                                  <div className="flex items-center gap-1 text-slate-500 text-xs mb-1">
                                    <MapPin className="w-3 h-3" />
                                    Sede / Residenza
                                  </div>
                                  <p className="font-medium">
                                    {user.indirizzo || '—'}
                                    {(user.citta || user.provincia) && (
                                      <span className="block text-slate-600">
                                        {[user.citta, user.provincia ? `(${user.provincia})` : null].filter(Boolean).join(' ')}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                          
                          {/* Pulsante Sblocca Quota: visibile per agenti che hanno raggiunto il limite */}
                          {user.role === 'agente' && user.quotazioni_count >= (user.quota_limit || 10) && canEditUser(currentUser, user) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              onClick={() => handleResetQuota(user.id, user.name)}
                              disabled={actionLoading === user.id}
                              title={`Sblocca quota (${user.quotazioni_count}/${user.quota_limit || 10})`}
                              data-testid={`reset-quota-${user.id}`}
                            >
                              {actionLoading === user.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          {/* Pulsante Modifica: visibile solo se hai permessi */}
                          {canEditUser(currentUser, user) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Pulsante Elimina: visibile solo se hai permessi */}
                          {canDeleteUser(currentUser, user) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(user.id)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Badge se non puoi fare nulla */}
                          {!canEditUser(currentUser, user) && !canDeleteUser(currentUser, user) && (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersPage;
